import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { settingsApi, usersApi } from '../lib/api';
import { useAuth } from '../hooks/useAuth';
import Button from '../components/ui/Button';
import Input, { Select } from '../components/ui/Input';
import ConfirmDialog from '../components/ui/ConfirmDialog';
import Modal from '../components/ui/Modal';
import { TEAMS } from '../lib/utils';

function SectionTitle({ children }: { children: React.ReactNode }) {
  return <h3 className="text-base font-semibold text-gray-900 mb-4">{children}</h3>;
}

function Card({ children, className }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className || ''}`}>
      {children}
    </div>
  );
}

// Password Change Section
const pwSchema = z.object({
  current_password: z.string().min(1, 'Required'),
  new_password: z.string().min(8, 'Min 8 characters'),
  confirm_password: z.string().min(1, 'Required'),
}).refine(d => d.new_password === d.confirm_password, {
  message: "Passwords don't match",
  path: ['confirm_password'],
});

function PasswordSection() {
  const mutation = useMutation({
    mutationFn: settingsApi.password,
    onSuccess: () => {
      toast.success('Password changed successfully');
      reset();
    },
    onError: (err: any) => toast.error(err.message),
  });

  const { register, handleSubmit, reset, formState: { errors } } = useForm({
    resolver: zodResolver(pwSchema),
  });

  return (
    <Card>
      <SectionTitle>Change Password</SectionTitle>
      <form onSubmit={handleSubmit((d: any) => mutation.mutate(d))} className="space-y-4 max-w-md">
        <Input
          label="Current Password"
          type="password"
          {...register('current_password')}
          error={errors.current_password?.message as string}
        />
        <Input
          label="New Password"
          type="password"
          {...register('new_password')}
          error={errors.new_password?.message as string}
        />
        <Input
          label="Confirm New Password"
          type="password"
          {...register('confirm_password')}
          error={errors.confirm_password?.message as string}
        />
        <Button type="submit" loading={mutation.isPending}>Change Password</Button>
      </form>
    </Card>
  );
}

// Team Members Section
function TeamMembersSection() {
  const qc = useQueryClient();
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [name, setName] = useState('');
  const [team, setTeam] = useState('SEO');

  const { data } = useQuery({
    queryKey: ['settings', 'team-members'],
    queryFn: settingsApi.teamMembers.list,
  });

  const createMutation = useMutation({
    mutationFn: settingsApi.teamMembers.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'team-members'] });
      setName('');
      toast.success('Team member added');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: settingsApi.teamMembers.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'team-members'] });
      setDeleteTarget(null);
      toast.success('Team member removed');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const members = data?.data || [];
  const grouped: Record<string, any[]> = {};
  for (const m of members) {
    if (!grouped[m.team]) grouped[m.team] = [];
    grouped[m.team].push(m);
  }

  return (
    <Card>
      <SectionTitle>Team Members</SectionTitle>

      {/* Add Member */}
      <div className="flex gap-3 mb-6 max-w-md">
        <input
          type="text"
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="Member name"
          className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <select
          value={team}
          onChange={e => setTeam(e.target.value)}
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        >
          {TEAMS.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <Button
          onClick={() => name.trim() && createMutation.mutate({ name: name.trim(), team })}
          loading={createMutation.isPending}
          disabled={!name.trim()}
          size="md"
        >
          Add
        </Button>
      </div>

      {/* Members by team */}
      <div className="space-y-4">
        {Object.entries(grouped).map(([teamName, teamMembers]) => (
          <div key={teamName}>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{teamName} Team</p>
            <div className="flex flex-wrap gap-2">
              {teamMembers.map(m => (
                <div
                  key={m.id}
                  className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
                >
                  <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center text-xs font-semibold text-primary-700">
                    {m.name.charAt(0)}
                  </div>
                  <span className="text-sm font-medium text-gray-700">{m.name}</span>
                  <button
                    onClick={() => setDeleteTarget(m)}
                    className="text-gray-400 hover:text-red-500 transition-colors ml-1"
                  >
                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              ))}
            </div>
          </div>
        ))}
        {members.length === 0 && (
          <p className="text-sm text-gray-400">No team members yet</p>
        )}
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget?.id)}
        title="Remove Team Member"
        message={`Remove "${deleteTarget?.name}" from the team?`}
        confirmLabel="Remove"
        loading={deleteMutation.isPending}
      />
    </Card>
  );
}

function WebsitesSection() {
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<{ name: string; url: string }>({ name: '', url: '' });
  const [deleteTarget, setDeleteTarget] = useState<any>(null);

  const { data } = useQuery({
    queryKey: ['settings', 'websites'],
    queryFn: settingsApi.websites.list,
  });

  const websites = data?.data || [];

  const createMutation = useMutation({
    mutationFn: settingsApi.websites.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'websites'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setName('');
      setUrl('');
      toast.success('Website added');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => settingsApi.websites.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'websites'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setEditingId(null);
      toast.success('Website updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: settingsApi.websites.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'websites'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
      setDeleteTarget(null);
      toast.success('Website archived');
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <Card>
      <SectionTitle>Websites</SectionTitle>
      <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 mb-6">
        <input
          type="text"
          value={name}
          onChange={event => setName(event.target.value)}
          placeholder="Website name, e.g. Reloadux"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <input
          type="url"
          value={url}
          onChange={event => setUrl(event.target.value)}
          placeholder="Base URL, e.g. https://reloadux.com"
          className="border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
        />
        <Button
          onClick={() => name.trim() && createMutation.mutate({ name: name.trim(), url: url.trim() || null })}
          loading={createMutation.isPending}
          disabled={!name.trim()}
        >
          Add Website
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Name</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Base URL</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {websites.map((site: any) => (
              <tr key={site.id} className="border-t border-gray-50">
                {editingId === site.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input
                        value={editValues.name}
                        onChange={event => setEditValues(values => ({ ...values, name: event.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        value={editValues.url}
                        onChange={event => setEditValues(values => ({ ...values, url: event.target.value }))}
                        className="w-full border border-gray-300 rounded px-2 py-1 text-sm font-mono"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => updateMutation.mutate({ id: site.id, data: { name: editValues.name, url: editValues.url || null } })}
                          className="text-xs text-primary-600 font-medium hover:text-primary-800"
                        >
                          Save
                        </button>
                        <button onClick={() => setEditingId(null)} className="text-xs text-gray-400 hover:text-gray-600">
                          Cancel
                        </button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{site.name}</td>
                    <td className="px-4 py-2.5">
                      {site.url ? (
                        <a href={site.url} target="_blank" rel="noopener noreferrer" className="font-mono text-xs text-blue-600 hover:underline">
                          {site.url}
                        </a>
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => {
                            setEditingId(site.id);
                            setEditValues({ name: site.name, url: site.url || '' });
                          }}
                          className="text-xs text-blue-500 font-medium hover:text-blue-700"
                        >
                          Edit
                        </button>
                        <span className="text-gray-200">|</span>
                        <button onClick={() => setDeleteTarget(site)} className="text-xs text-red-400 font-medium hover:text-red-600">
                          Archive
                        </button>
                      </div>
                    </td>
                  </>
                )}
              </tr>
            ))}
            {websites.length === 0 && (
              <tr>
                <td colSpan={3} className="px-4 py-8 text-center text-sm text-gray-400">No websites yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget?.id)}
        title="Archive Website"
        message={`Archive "${deleteTarget?.name}"? Existing tasks will keep this website, but it will no longer appear when creating new tasks.`}
        confirmLabel="Archive"
        loading={deleteMutation.isPending}
      />
    </Card>
  );
}

// Page Types Section
function PageTypesSection() {
  const qc = useQueryClient();
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editValues, setEditValues] = useState<any>({});

  const { data } = useQuery({
    queryKey: ['page-types'],
    queryFn: async () => {
      const { pageTypesApi } = await import('../lib/api');
      return pageTypesApi.list();
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) =>
      settingsApi.pageTypes.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['page-types'] });
      setEditingId(null);
      toast.success('Page type updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const pageTypes = data?.data || [];

  return (
    <Card>
      <SectionTitle>Page Types</SectionTitle>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50 rounded-lg">
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Priority</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Name</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Total Pages</th>
              <th className="text-left px-4 py-2 text-xs font-semibold text-gray-500 uppercase">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageTypes.map((pt: any) => (
              <tr key={pt.id} className="border-t border-gray-50">
                {editingId === pt.id ? (
                  <>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={editValues.priority ?? pt.priority}
                        onChange={e => setEditValues((v: any) => ({ ...v, priority: parseInt(e.target.value) }))}
                        className="w-16 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="text"
                        value={editValues.name ?? pt.name}
                        onChange={e => setEditValues((v: any) => ({ ...v, name: e.target.value }))}
                        className="border border-gray-300 rounded px-2 py-1 text-sm w-full max-w-xs"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <input
                        type="number"
                        value={editValues.total_pages ?? pt.total_pages}
                        onChange={e => setEditValues((v: any) => ({ ...v, total_pages: parseInt(e.target.value) }))}
                        className="w-24 border border-gray-300 rounded px-2 py-1 text-sm"
                      />
                    </td>
                    <td className="px-4 py-2">
                      <div className="flex gap-2">
                        <button
                          onClick={() => updateMutation.mutate({ id: pt.id, data: editValues })}
                          className="text-xs text-primary-600 font-medium hover:text-primary-800"
                        >Save</button>
                        <button
                          onClick={() => { setEditingId(null); setEditValues({}); }}
                          className="text-xs text-gray-400 hover:text-gray-600"
                        >Cancel</button>
                      </div>
                    </td>
                  </>
                ) : (
                  <>
                    <td className="px-4 py-2.5 text-gray-500">{pt.priority}</td>
                    <td className="px-4 py-2.5 font-medium text-gray-800">{pt.name}</td>
                    <td className="px-4 py-2.5 text-gray-600">{pt.total_pages}</td>
                    <td className="px-4 py-2.5">
                      <button
                        onClick={() => { setEditingId(pt.id); setEditValues({ name: pt.name, total_pages: pt.total_pages, priority: pt.priority }); }}
                        className="text-xs text-blue-500 font-medium hover:text-blue-700"
                      >Edit</button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

// General Settings Section
function GeneralSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'general'],
    queryFn: settingsApi.general.get,
  });

  const mutation = useMutation({
    mutationFn: settingsApi.general.update,
    onSuccess: () => toast.success('Settings saved'),
    onError: (err: any) => toast.error(err.message),
  });

  const [values, setValues] = useState<Record<string, string>>({});
  const settings = data?.data || {};

  React.useEffect(() => {
    if (data?.data) setValues(data.data);
  }, [data]);

  return (
    <Card>
      <SectionTitle>General Settings</SectionTitle>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : (
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site Name</label>
            <input
              type="text"
              value={values.site_name || ''}
              onChange={e => setValues(v => ({ ...v, site_name: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Site URL</label>
            <input
              type="url"
              value={values.site_url || ''}
              onChange={e => setValues(v => ({ ...v, site_url: e.target.value }))}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
            />
          </div>
          <Button
            onClick={() => mutation.mutate(values)}
            loading={mutation.isPending}
          >
            Save Settings
          </Button>
        </div>
      )}
    </Card>
  );
}

// AI Settings Section
function AiSettingsSection() {
  const { data, isLoading } = useQuery({
    queryKey: ['settings', 'general'],
    queryFn: settingsApi.general.get,
  });

  const mutation = useMutation({
    mutationFn: settingsApi.general.update,
    onSuccess: () => toast.success('AI settings saved'),
    onError: (err: any) => toast.error(err.message),
  });

  const [apiKey, setApiKey] = useState('');
  const [showKey, setShowKey] = useState(false);

  React.useEffect(() => {
    if (data?.data?.openrouter_api_key) {
      setApiKey(data.data.openrouter_api_key);
    }
  }, [data]);

  const handleSave = () => {
    mutation.mutate({ openrouter_api_key: apiKey.trim() });
  };

  return (
    <Card>
      <SectionTitle>AI Settings</SectionTitle>
      <p className="text-sm text-gray-500 mb-4">
        Used for the <span className="font-medium text-orange-600">✨ AI Improve</span> button in notes — converts Roman Urdu to professional English.
        Get a free key from{' '}
        <a href="https://openrouter.ai/keys" target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">openrouter.ai</a>.
      </p>
      {isLoading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : (
        <div className="space-y-4 max-w-md">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">OpenRouter API Key</label>
            <div className="relative">
              <input
                type={showKey ? 'text' : 'password'}
                value={apiKey}
                onChange={e => setApiKey(e.target.value)}
                placeholder="sk-or-v1-..."
                className="w-full border border-gray-300 rounded-lg px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400 focus:border-orange-400"
              />
              <button
                type="button"
                onClick={() => setShowKey(v => !v)}
                className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                title={showKey ? 'Hide key' : 'Show key'}
              >
                {showKey ? (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 4.411m0 0L21 21" /></svg>
                ) : (
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
                )}
              </button>
            </div>
            {apiKey && (
              <p className="mt-1 text-xs text-green-600 flex items-center gap-1">
                <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" /></svg>
                API key configured
              </p>
            )}
          </div>
          <Button
            onClick={handleSave}
            loading={mutation.isPending}
            disabled={!apiKey.trim()}
          >
            Save API Key
          </Button>
        </div>
      )}
    </Card>
  );
}

// Data Cleanup Section
function CleanupSection() {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const mutation = useMutation({
    mutationFn: settingsApi.cleanup,
    onSuccess: (data: any) => {
      setConfirmOpen(false);
      const total = (data.deleted.sitewide || 0) + (data.deleted.pageTasks || 0);
      toast.success(`Cleaned up ${total} completed tasks older than 30 days`);
    },
    onError: (err: any) => toast.error(err.message),
  });

  return (
    <Card>
      <SectionTitle>Data Management</SectionTitle>
      <div className="flex items-start gap-4 p-4 bg-red-50 border border-red-200 rounded-lg">
        <svg className="w-5 h-5 text-red-500 mt-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
        </svg>
        <div className="flex-1">
          <p className="font-medium text-red-800">Cleanup Completed Tasks</p>
          <p className="text-sm text-red-600 mt-1">Permanently delete all tasks with status "Done" that were completed more than 30 days ago. This action cannot be undone.</p>
          <Button
            variant="danger"
            size="sm"
            className="mt-3"
            onClick={() => setConfirmOpen(true)}
          >
            Run Cleanup
          </Button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmOpen}
        onClose={() => setConfirmOpen(false)}
        onConfirm={() => mutation.mutate()}
        title="Run Data Cleanup"
        message="This will permanently delete all tasks marked as Done that were completed more than 30 days ago. This cannot be undone. Are you sure?"
        confirmLabel="Yes, Clean Up"
        loading={mutation.isPending}
      />
    </Card>
  );
}

// ─── User Accounts Section (admin only) ──────────────────────────────────────

type UserFormValues = {
  username: string;
  password: string;
  role: 'admin' | 'member';
  team_member_name: string;
  display_name: string;
};

type EditUserFormValues = {
  role: 'admin' | 'member';
  team_member_name: string;
  display_name: string;
  password: string;
};

function UserAccountsSection() {
  const qc = useQueryClient();
  const [showCreate, setShowCreate] = useState(false);
  const [editTarget, setEditTarget] = useState<any>(null);
  const [deleteTarget, setDeleteTarget] = useState<any>(null);
  const [createdCreds, setCreatedCreds] = useState<{ username: string; password: string } | null>(null);
  const [showCreatePass, setShowCreatePass] = useState(false);

  const { data: usersData } = useQuery({
    queryKey: ['settings', 'users'],
    queryFn: usersApi.list,
  });

  const { data: membersData } = useQuery({
    queryKey: ['settings', 'team-members'],
    queryFn: settingsApi.teamMembers.list,
  });

  const users: any[] = usersData?.data || [];
  const teamMembers: any[] = membersData?.data || [];

  const createMutation = useMutation({
    mutationFn: usersApi.create,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'users'] });
      setCreatedCreds({ username: createForm.username, password: createForm.password });
      setShowCreate(false);
      setCreateForm({ username: '', password: '', role: 'member', team_member_name: '', display_name: '' });
    },
    onError: (err: any) => toast.error(err.message),
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: any }) => usersApi.update(id, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'users'] });
      setEditTarget(null);
      toast.success('User updated');
    },
    onError: (err: any) => toast.error(err.message),
  });

  const deleteMutation = useMutation({
    mutationFn: usersApi.delete,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'users'] });
      setDeleteTarget(null);
      toast.success('User deleted');
    },
    onError: (err: any) => toast.error(err.message),
  });

  // Create form
  const [createForm, setCreateForm] = useState<UserFormValues>({
    username: '',
    password: '',
    role: 'member',
    team_member_name: '',
    display_name: '',
  });

  // Edit form
  const [editForm, setEditForm] = useState<EditUserFormValues>({
    role: 'member',
    team_member_name: '',
    display_name: '',
    password: '',
  });

  function openEdit(user: any) {
    setEditTarget(user);
    setEditForm({
      role: user.role || 'member',
      team_member_name: user.team_member_name || '',
      display_name: user.display_name || '',
      password: '',
    });
  }

  function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!createForm.username.trim() || !createForm.password.trim()) {
      toast.error('Username and password are required');
      return;
    }
    createMutation.mutate({
      username: createForm.username.trim(),
      password: createForm.password,
      role: createForm.role,
      team_member_name: createForm.team_member_name || null,
      display_name: createForm.display_name || null,
    });
  }

  function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    const payload: any = {
      role: editForm.role,
      team_member_name: editForm.team_member_name || null,
      display_name: editForm.display_name || null,
    };
    if (editForm.password.trim()) payload.password = editForm.password;
    updateMutation.mutate({ id: editTarget.id, data: payload });
  }

  const inputCls = 'w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400';
  const labelCls = 'block text-sm font-medium text-gray-700 mb-1';

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <SectionTitle>User Accounts</SectionTitle>
        <button
          onClick={() => { setCreateForm({ username: '', password: '', role: 'member', team_member_name: '', display_name: '' }); setShowCreate(true); }}
          className="inline-flex items-center gap-1.5 text-sm font-semibold text-white px-3.5 py-2 rounded-lg transition-all"
          style={{ backgroundColor: '#F97316' }}
          onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EA6C0A')}
          onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#F97316')}
        >
          <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add User
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-gray-50">
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Username</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Display Name</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Linked Member</th>
              <th className="text-left px-4 py-2.5 text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-50">
            {users.map((u: any) => (
              <tr key={u.id} className="hover:bg-gray-50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <div
                      className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                      style={{ backgroundColor: '#F97316' }}
                    >
                      {u.username?.charAt(0).toUpperCase()}
                    </div>
                    <span className="font-medium text-gray-800">{u.username}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-gray-600">{u.display_name || <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-3">
                  <span
                    className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-semibold"
                    style={u.role === 'admin'
                      ? { backgroundColor: '#FFF7ED', color: '#C2410C', border: '1px solid #FED7AA' }
                      : { backgroundColor: '#F0FDF4', color: '#166534', border: '1px solid #BBF7D0' }}
                  >
                    {u.role === 'admin' ? 'Admin' : 'Member'}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-600">{u.team_member_name || <span className="text-gray-300">—</span>}</td>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => openEdit(u)}
                      className="text-xs text-blue-500 font-medium hover:text-blue-700 transition-colors"
                    >
                      Edit
                    </button>
                    <span className="text-gray-200">|</span>
                    <button
                      onClick={() => setDeleteTarget(u)}
                      className="text-xs text-red-400 font-medium hover:text-red-600 transition-colors"
                    >
                      Delete
                    </button>
                  </div>
                </td>
              </tr>
            ))}
            {users.length === 0 && (
              <tr>
                <td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">No users yet</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Create User Modal */}
      <Modal open={showCreate} onClose={() => setShowCreate(false)} title="Add New User" size="md">
        <form onSubmit={handleCreate} className="space-y-4">
          <div>
            <label className={labelCls}>Username <span className="text-red-500">*</span></label>
            <input
              className={inputCls}
              value={createForm.username}
              onChange={e => setCreateForm(f => ({ ...f, username: e.target.value }))}
              placeholder="e.g. seo1"
            />
          </div>
          <div>
            <label className={labelCls}>Password <span className="text-red-500">*</span></label>
            <div className="relative">
              <input
                type={showCreatePass ? 'text' : 'password'}
                className={inputCls}
                value={createForm.password}
                onChange={e => setCreateForm(f => ({ ...f, password: e.target.value }))}
                placeholder="Min 6 characters"
                style={{ paddingRight: '40px' }}
              />
              <button
                type="button"
                onClick={() => setShowCreatePass(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 text-xs"
              >
                {showCreatePass ? '🙈 Hide' : '👁 Show'}
              </button>
            </div>
          </div>
          <div>
            <label className={labelCls}>Display Name</label>
            <input
              className={inputCls}
              value={createForm.display_name}
              onChange={e => setCreateForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="e.g. Ahmed (SEO-1)"
            />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select
              className={inputCls}
              value={createForm.role}
              onChange={e => setCreateForm(f => ({ ...f, role: e.target.value as 'admin' | 'member' }))}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Linked Team Member</label>
            <select
              className={inputCls}
              value={createForm.team_member_name}
              onChange={e => setCreateForm(f => ({ ...f, team_member_name: e.target.value }))}
            >
              <option value="">— None —</option>
              {teamMembers.map((m: any) => (
                <option key={m.id} value={m.name}>{m.name} ({m.team})</option>
              ))}
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setShowCreate(false)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all disabled:opacity-50"
              style={{ backgroundColor: '#F97316' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EA6C0A')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#F97316')}
            >
              {createMutation.isPending ? 'Creating...' : 'Create User'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Edit User Modal */}
      <Modal open={!!editTarget} onClose={() => setEditTarget(null)} title={`Edit User: ${editTarget?.username}`} size="md">
        <form onSubmit={handleUpdate} className="space-y-4">
          <div>
            <label className={labelCls}>Display Name</label>
            <input
              className={inputCls}
              value={editForm.display_name}
              onChange={e => setEditForm(f => ({ ...f, display_name: e.target.value }))}
              placeholder="e.g. Ahmed (SEO-1)"
            />
          </div>
          <div>
            <label className={labelCls}>Role</label>
            <select
              className={inputCls}
              value={editForm.role}
              onChange={e => setEditForm(f => ({ ...f, role: e.target.value as 'admin' | 'member' }))}
            >
              <option value="member">Member</option>
              <option value="admin">Admin</option>
            </select>
          </div>
          <div>
            <label className={labelCls}>Linked Team Member</label>
            <select
              className={inputCls}
              value={editForm.team_member_name}
              onChange={e => setEditForm(f => ({ ...f, team_member_name: e.target.value }))}
            >
              <option value="">— None —</option>
              {teamMembers.map((m: any) => (
                <option key={m.id} value={m.name}>{m.name} ({m.team})</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>New Password <span className="text-gray-400 font-normal text-xs">(leave blank to keep current)</span></label>
            <input
              type="password"
              className={inputCls}
              value={editForm.password}
              onChange={e => setEditForm(f => ({ ...f, password: e.target.value }))}
              placeholder="Leave blank to keep unchanged"
            />
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={() => setEditTarget(null)} className="px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-800">
              Cancel
            </button>
            <button
              type="submit"
              disabled={updateMutation.isPending}
              className="px-4 py-2 text-sm font-semibold text-white rounded-lg transition-all disabled:opacity-50"
              style={{ backgroundColor: '#F97316' }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#EA6C0A')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = '#F97316')}
            >
              {updateMutation.isPending ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </Modal>

      {/* Delete confirm */}
      <ConfirmDialog
        open={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onConfirm={() => deleteMutation.mutate(deleteTarget?.id)}
        title="Delete User"
        message={`Are you sure you want to delete user "${deleteTarget?.username}"? This cannot be undone.`}
        confirmLabel="Delete User"
        loading={deleteMutation.isPending}
      />

      {/* ── Credentials Popup ── */}
      {createdCreds && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm p-6">
            <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7"/>
              </svg>
            </div>
            <h3 className="text-center font-bold text-gray-900 text-lg mb-1">User Created!</h3>
            <p className="text-center text-sm text-gray-500 mb-5">Ye credentials team member ko share karo</p>

            <div className="space-y-3 bg-gray-50 rounded-xl p-4 border border-gray-200">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 font-medium">Username</span>
                <span className="font-mono font-bold text-gray-900 text-sm">{createdCreds.username}</span>
              </div>
              <div className="border-t border-gray-200"/>
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 font-medium">Password</span>
                <span className="font-mono font-bold text-orange-600 text-sm">{createdCreds.password}</span>
              </div>
            </div>

            <div className="flex gap-2 mt-5">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(`Username: ${createdCreds.username}\nPassword: ${createdCreds.password}`);
                  toast.success('Credentials copied!');
                }}
                className="flex-1 py-2 text-sm font-semibold border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                📋 Copy
              </button>
              <button
                onClick={() => setCreatedCreds(null)}
                className="flex-1 py-2 text-sm font-semibold text-white rounded-lg transition-colors"
                style={{ backgroundColor: '#F97316' }}
              >
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = (user as any)?.role !== 'member';

  return (
    <div className="max-w-4xl space-y-6">
      <GeneralSection />
      <PasswordSection />
      {isAdmin && <UserAccountsSection />}
      {isAdmin && <WebsitesSection />}
      <TeamMembersSection />
      <PageTypesSection />
      {isAdmin && <AiSettingsSection />}
      <CleanupSection />
    </div>
  );
}
